import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, Upload, FileMusic, FileText, Layers, Package,
  Paperclip, Send, ShieldCheck, X as XIcon,
} from "lucide-react";
import { publicApi, errMsg } from "@/lib/api";

// ---------------------------------------------------------------------------
// Kandake — Demo & Development Intake (MP8) — public submission page.
//
// Visual language deliberately mirrors /kandake (Kandake Compliance Services):
//   * ink/charcoal ground, warm-cream type, burgundy emphasis, antique-gold rules.
//   * Playfair Display for headlines, Figtree for body.
//   * No internal AI screening status, no risk levels, no provider info.
//   * Single neutral confirmation message exactly per spec.
// ---------------------------------------------------------------------------

const INK     = "hsl(20 14% 6%)";
const INK_2   = "hsl(20 12% 9%)";
const CHAR    = "hsl(20 10% 16%)";
const CREAM   = "hsl(36 33% 96%)";
const CREAM_2 = "hsl(36 22% 93%)";
const BURG    = "hsl(347 60% 22%)";
const GOLD    = "hsl(40 48% 58%)";

const KANDAKE_LOGO = "/brand/kandake-compliance.png";

const FONT_LINK_HREF =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Figtree:wght@300;400;500;600&display=swap";

const INPUT_STYLE = {
  background: INK_2,
  color: CREAM,
  border: `1px solid ${GOLD}33`,
  borderRadius: 6,
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
};
const INPUT_STYLE_INVALID = { ...INPUT_STYLE, borderColor: BURG };

const FILE_KIND_LABELS = {
  audio:      "Audio demo (required)",
  lyrics:     "Lyrics sheet (optional)",
  stems:      "Stems archive (optional)",
  daw:        "DAW project (optional)",
  supporting: "Supporting document",
};

const FILE_KIND_ICONS = {
  audio:      FileMusic,
  lyrics:     FileText,
  stems:      Layers,
  daw:        Package,
  supporting: Paperclip,
};

const BLANK_FORM = {
  submitter_full_legal_name: "",
  artist_producer_name: "",
  email: "",
  phone: "",
  song_title: "",
  alternate_title: "",
  primary_genre: "",
  approximate_completion_date: "",
  development_notes: "",
  known_contributors: "",
  known_third_party_elements: "",
  has_authority_to_submit: false,
  rights_and_origin_attested: false,
  review_consent: false,
  website: "",
};

const BLANK_FILES = {
  audio: null,
  lyrics: null,
  stems: null,
  daw_project: null,
  supporting_doc_1: null,
  supporting_doc_2: null,
  supporting_doc_3: null,
};

function GoldRule({ width = 36 }) {
  return (
    <span aria-hidden="true" style={{ display: "inline-block", width, height: 1, background: GOLD, verticalAlign: "middle" }} />
  );
}

function Eyebrow({ children, dataTestId }) {
  return (
    <div
      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em]"
      style={{ color: `${CREAM}b0` }}
      data-testid={dataTestId}
    >
      <GoldRule width={20} />
      {children}
    </div>
  );
}

function Header() {
  return (
    <header
      className="sticky top-0 z-30 backdrop-blur"
      style={{ background: `${INK}cc`, borderBottom: `1px solid ${GOLD}26` }}
      data-testid="submit-demo-header"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between gap-4">
        <Link
          to="/kandake"
          className="inline-flex items-center gap-3 focus:outline-none"
          data-testid="submit-demo-back-to-kandake"
          aria-label="Return to Kandake Compliance Services"
        >
          <img src={KANDAKE_LOGO} alt="Kandake" style={{ height: 32, width: "auto" }} draggable={false} />
          <div className="hidden sm:block">
            <div style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 16, lineHeight: 1.05 }}>
              Kandake
            </div>
            <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: `${GOLD}cc` }}>
              Demo &amp; Development Intake
            </div>
          </div>
        </Link>
        <Link
          to="/kandake"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] hover:opacity-90 focus:outline-none"
          style={{ color: `${CREAM}cc` }}
          data-testid="submit-demo-header-return"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Return
        </Link>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer
      data-testid="submit-demo-footer"
      style={{ background: INK_2, color: `${CREAM}99`, borderTop: `1px solid ${GOLD}26` }}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10 grid gap-6 sm:grid-cols-[2fr_1fr]">
        <div>
          <div style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 18 }}>
            Kandake Compliance Services
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.22em]" style={{ color: `${GOLD}cc` }}>
            A service of The Durban 49 Group, LLC
          </div>
          <p className="mt-4 max-w-md leading-relaxed" style={{ color: `${CREAM}b3`, fontSize: 12.5 }}>
            Submissions are private. No commercial use, public listing, or licence is granted by submitting
            material. Files are stored securely and reviewed internally before any next step is communicated.
          </p>
        </div>
        <div className="text-[12px]" style={{ color: `${CREAM}99` }}>
          <div className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: `${GOLD}cc` }}>
            Need help?
          </div>
          <p className="leading-relaxed">
            If you cannot submit, please return to{" "}
            <Link to="/kandake" className="underline" style={{ color: GOLD }}>
              kandake.compliance
            </Link>{" "}
            and request a consultation. We will follow up directly.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FormLabel({ htmlFor, required, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[10.5px] uppercase tracking-[0.2em] mb-1.5"
      style={{ color: `${GOLD}cc` }}
    >
      {children}
      {required && <span style={{ color: BURG, marginLeft: 4 }} aria-hidden="true">*</span>}
    </label>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-1 text-[11.5px]" style={{ color: BURG, fontWeight: 500 }} role="alert">
      {message}
    </p>
  );
}

function FormField({
  id, label, required, type = "text", value, onChange, autoComplete, maxLength, placeholder, error, dataTestId,
}) {
  const errId = `${id}-error`;
  return (
    <div>
      <FormLabel htmlFor={`sd-${id}`} required={required}>{label}</FormLabel>
      <input
        id={`sd-${id}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        required={required}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? errId : undefined}
        style={error ? INPUT_STYLE_INVALID : INPUT_STYLE}
        data-testid={dataTestId}
      />
      <FieldError id={errId} message={error} />
    </div>
  );
}

function FormTextarea({ id, label, value, onChange, rows = 4, placeholder, maxLength, dataTestId, error }) {
  const errId = `${id}-error`;
  return (
    <div>
      <FormLabel htmlFor={`sd-${id}`}>{label}</FormLabel>
      <textarea
        id={`sd-${id}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={!!error}
        aria-describedby={error ? errId : undefined}
        style={{ ...(error ? INPUT_STYLE_INVALID : INPUT_STYLE), minHeight: 96, resize: "vertical" }}
        data-testid={dataTestId}
      />
      <FieldError id={errId} message={error} />
    </div>
  );
}

function humanSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes >= 1024 * 1024 * 1024) return (bytes / (1024 ** 3)).toFixed(2) + " GB";
  if (bytes >= 1024 * 1024) return (bytes / (1024 ** 2)).toFixed(1) + " MB";
  if (bytes >= 1024) return (bytes / 1024).toFixed(1) + " KB";
  return `${bytes} B`;
}

function FilePicker({
  id, kind, label, required, file, onPick, allowedHint, capLabel, error, dataTestId, slot = "audio",
}) {
  const Icon = FILE_KIND_ICONS[kind] || Paperclip;
  const inputId = `sd-file-${id}`;
  // Map the file kind to an HTML accept attribute. We list extension + MIME
  // family so the OS picker shows the right files without limiting power users
  // (the backend still re-validates extensions + MIME prefixes server-side).
  const ACCEPT_BY_KIND = {
    audio:      "audio/*,.wav,.mp3,.m4a,.aiff,.aif,.flac,.aac,.ogg",
    lyrics:     ".txt,.pdf,.docx,.rtf",
    stems:      ".zip,.rar,.7z",
    daw:        ".zip,.rar,.7z,.als,.flp,.logicx,.cpr,.ptx",
    supporting: ".pdf,.docx,.txt,.png,.jpg,.jpeg,.zip",
  };
  const acceptAttr = ACCEPT_BY_KIND[kind] || undefined;
  return (
    <div
      className="rounded-md p-4"
      style={{
        background: INK_2,
        border: `1px solid ${error ? BURG : `${GOLD}33`}`,
      }}
      data-testid={dataTestId}
    >
      <div className="flex items-start gap-3">
        <Icon className="w-4 h-4 mt-0.5" style={{ color: GOLD }} aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <FormLabel htmlFor={inputId} required={required}>
            {label}
          </FormLabel>
          <p className="text-[11px] leading-relaxed" style={{ color: `${CREAM}99` }}>
            {allowedHint}{capLabel ? ` · up to ${capLabel}` : ""}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label
              htmlFor={inputId}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] cursor-pointer"
              style={{ background: `${GOLD}1a`, color: CREAM, border: `1px solid ${GOLD}55` }}
              data-testid={`${dataTestId}-button`}
            >
              <Upload className="w-3.5 h-3.5" />
              {file ? "Replace file" : "Choose file"}
            </label>
            <input
              id={inputId}
              name={slot}
              type="file"
              accept={acceptAttr}
              onChange={(e) => onPick(e.target.files?.[0] || null)}
              className="sr-only"
              data-testid={`${dataTestId}-input`}
            />
            {file && (
              <button
                type="button"
                onClick={() => onPick(null)}
                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] opacity-80 hover:opacity-100"
                style={{ color: CREAM }}
                data-testid={`${dataTestId}-remove`}
              >
                <XIcon className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
          {file && (
            <div className="mt-2 text-[12px] truncate" style={{ color: CREAM }} data-testid={`${dataTestId}-summary`}>
              <span style={{ color: `${CREAM}99` }}>Selected:</span>{" "}
              <span className="font-medium">{file.name}</span>
              <span style={{ color: `${CREAM}99` }}> · {humanSize(file.size)}</span>
            </div>
          )}
        </div>
      </div>
      <FieldError id={`${inputId}-error`} message={error} />
    </div>
  );
}

function AttestationCheckbox({ id, checked, onToggle, error, children, dataTestId }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="flex items-start gap-3 cursor-pointer"
        style={{ color: CREAM }}
        data-testid={`${dataTestId}-label`}
      >
        <input
          id={id}
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-1"
          style={{ accentColor: GOLD }}
          data-testid={dataTestId}
        />
        <span className="text-[13px] leading-relaxed">{children}</span>
      </label>
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

export default function KandakeSubmitDemo() {
  const [form, setForm] = useState(BLANK_FORM);
  const [files, setFiles] = useState(BLANK_FILES);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(null);
  const [meta, setMeta] = useState({ size_caps: {}, extensions: {} });
  const mountedAtRef = useRef(Date.now());

  // Inject Google Fonts for Playfair Display + Figtree (same as /kandake).
  useEffect(() => {
    const id = "kc-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = FONT_LINK_HREF;
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    publicApi.get("/public/demos/meta")
      .then((r) => { if (!cancelled) setMeta(r.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  function setField(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  }
  function setFileSlot(k, v) {
    setFiles((s) => ({ ...s, [k]: v }));
    if (errors[`file_${k}`]) setErrors((e) => ({ ...e, [`file_${k}`]: undefined }));
  }

  function capLabelFor(kind) {
    const cap = meta?.size_caps?.[kind];
    if (!cap) return "";
    const mb = cap / (1024 * 1024);
    return `${Math.round(mb)} MB`;
  }
  function allowedHintFor(kind) {
    const exts = meta?.extensions?.[kind];
    if (Array.isArray(exts) && exts.length) return exts.join(", ");
    return "";
  }

  function validate() {
    const e = {};
    if (!form.submitter_full_legal_name.trim()) e.submitter_full_legal_name = "Please enter your full legal name.";
    if (!form.email.trim()) e.email = "Please provide an email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = "Please enter a valid email address.";
    if (!form.song_title.trim()) e.song_title = "Please enter a working title for the recording.";
    if (!files.audio) e.file_audio = "An audio demo file is required.";
    if (!form.has_authority_to_submit) e.has_authority_to_submit = "Authority confirmation is required.";
    if (!form.rights_and_origin_attested) e.rights_and_origin_attested = "Rights-and-origin attestation is required.";
    if (!form.review_consent) e.review_consent = "Review consent is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(ev) {
    ev.preventDefault();
    setSubmitError("");
    if (!validate()) {
      const firstKey = Object.keys(errors)[0];
      if (firstKey) {
        const el = document.getElementById(firstKey.startsWith("file_") ? `sd-file-${firstKey.slice(5)}` : `sd-${firstKey}`);
        if (el && typeof el.focus === "function") el.focus();
      }
      return;
    }
    setSubmitting(true);
    try {
      const elapsed = Math.max(0, Date.now() - mountedAtRef.current);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (typeof v === "boolean") fd.append(k, v ? "true" : "false");
        else fd.append(k, v ?? "");
      });
      fd.append("form_elapsed_ms", String(elapsed));
      if (files.audio)            fd.append("audio", files.audio);
      if (files.lyrics)           fd.append("lyrics", files.lyrics);
      if (files.stems)            fd.append("stems", files.stems);
      if (files.daw_project)      fd.append("daw_project", files.daw_project);
      if (files.supporting_doc_1) fd.append("supporting_doc_1", files.supporting_doc_1);
      if (files.supporting_doc_2) fd.append("supporting_doc_2", files.supporting_doc_2);
      if (files.supporting_doc_3) fd.append("supporting_doc_3", files.supporting_doc_3);

      const r = await publicApi.post("/public/demos/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      setSuccess(r.data);
      setForm(BLANK_FORM);
      setFiles(BLANK_FILES);
      mountedAtRef.current = Date.now();
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* ignore */ }
    } catch (e) {
      setSubmitError(errMsg(e, "Unable to submit. Please check the form and try again."));
    } finally {
      setSubmitting(false);
    }
  }

  const totalSelectedBytes = useMemo(() => {
    return Object.values(files).reduce((acc, f) => acc + (f?.size || 0), 0);
  }, [files]);

  return (
    <div style={{ background: INK, color: CREAM, minHeight: "100vh", fontFamily: "'Figtree', system-ui, sans-serif" }} data-testid="submit-demo-page">
      <Header />

      <section style={{ background: INK }} data-testid="submit-demo-hero">
        <div className="max-w-5xl mx-auto px-6 sm:px-10 pt-14 pb-8">
          <Eyebrow dataTestId="submit-demo-eyebrow">Demo &amp; Development Intake</Eyebrow>
          <h1
            className="mt-4 leading-tight"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(28px, 4vw, 44px)",
              fontWeight: 600,
              color: CREAM,
              letterSpacing: "-0.005em",
            }}
            data-testid="submit-demo-headline"
          >
            Submit a demo for internal compliance review.
          </h1>
          <p
            className="mt-5 max-w-3xl leading-relaxed"
            style={{ color: `${CREAM}b3`, fontSize: 15 }}
            data-testid="submit-demo-subhead"
          >
            Kandake reviews each submission privately, with the same compliance discipline applied to commercial
            catalog records. Submitting a demo does not grant any licence, registration, clearance, release
            approval, or commercial use. Material is held securely. We will contact you only if additional
            information is required.
          </p>
        </div>
      </section>

      <section data-testid="submit-demo-form-section" style={{ background: INK }}>
        <div className="max-w-3xl mx-auto px-6 sm:px-10 pb-24">
          {success ? (
            <div
              className="rounded-2xl px-6 sm:px-10 py-10"
              style={{ background: INK_2, border: `1px solid ${GOLD}55` }}
              role="status"
              data-testid="submit-demo-success"
            >
              <CheckCircle2 className="w-8 h-8 mb-4" style={{ color: GOLD }} aria-hidden="true" />
              <div className="text-[10px] uppercase tracking-[0.22em] mb-2" style={{ color: `${GOLD}cc` }}>
                Received
              </div>
              <h2
                style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 26, fontWeight: 600, lineHeight: 1.2 }}
                data-testid="submit-demo-success-headline"
              >
                Your submission has been received and is under initial review.
              </h2>
              <div
                className="mt-6 inline-flex flex-col rounded-md px-4 py-3"
                style={{ background: `${GOLD}10`, border: `1px dashed ${GOLD}66` }}
                data-testid="submit-demo-success-ref"
              >
                <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: `${GOLD}cc` }}>
                  Internal reference
                </div>
                <div className="font-mono text-[18px] tracking-wider" style={{ color: CREAM }} data-testid="submit-demo-success-ref-id">
                  {success.ref_id || "—"}
                </div>
                <div className="text-[11px] mt-1" style={{ color: `${CREAM}99` }}>
                  Please retain your reference ID for any future correspondence.
                </div>
              </div>
              <p className="mt-6 leading-relaxed" style={{ color: `${CREAM}cc`, fontSize: 14 }} data-testid="submit-demo-success-copy">
                We will contact you if additional documentation is required. This submission does not grant any
                licence, registration, clearance, release approval, or commercial use.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => { setSuccess(null); mountedAtRef.current = Date.now(); try { window.scrollTo({ top: 0 }); } catch { /* ignore */ } }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.22em] hover:opacity-90"
                  style={{ background: "transparent", color: CREAM, border: `1px solid ${GOLD}66` }}
                  data-testid="submit-demo-success-new"
                >
                  Submit another demo
                </button>
                <Link
                  to="/kandake"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.22em] hover:opacity-90"
                  style={{ background: GOLD, color: INK, border: `1px solid ${GOLD}` }}
                  data-testid="submit-demo-success-home"
                >
                  Return to Kandake
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-12" data-testid="submit-demo-form">
              {submitError && (
                <div
                  role="alert"
                  className="rounded-md px-4 py-3 text-[13px]"
                  style={{ background: `${BURG}33`, border: `1px solid ${BURG}88`, color: CREAM }}
                  data-testid="submit-demo-error"
                >
                  {submitError}
                </div>
              )}

              {/* Section 1: Submitter */}
              <div data-testid="submit-demo-section-submitter">
                <Eyebrow>1 — Submitter</Eyebrow>
                <h2 className="mt-3" style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 24, fontWeight: 600 }}>
                  Who is sending this material
                </h2>
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <FormField
                    id="submitter_full_legal_name" label="Full legal name" required maxLength={200} autoComplete="name"
                    value={form.submitter_full_legal_name}
                    onChange={(v) => setField("submitter_full_legal_name", v)}
                    error={errors.submitter_full_legal_name}
                    dataTestId="submit-demo-field-legal-name"
                  />
                  <FormField
                    id="artist_producer_name" label="Artist / producer name (if different)" maxLength={200}
                    value={form.artist_producer_name}
                    onChange={(v) => setField("artist_producer_name", v)}
                    dataTestId="submit-demo-field-artist"
                  />
                  <FormField
                    id="email" label="Email address" required type="email" maxLength={200} autoComplete="email"
                    value={form.email}
                    onChange={(v) => setField("email", v)}
                    error={errors.email}
                    dataTestId="submit-demo-field-email"
                  />
                  <FormField
                    id="phone" label="Phone (optional)" type="tel" maxLength={60} autoComplete="tel"
                    value={form.phone}
                    onChange={(v) => setField("phone", v)}
                    dataTestId="submit-demo-field-phone"
                  />
                </div>
              </div>

              {/* Section 2: Recording */}
              <div data-testid="submit-demo-section-recording">
                <Eyebrow>2 — Recording</Eyebrow>
                <h2 className="mt-3" style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 24, fontWeight: 600 }}>
                  Tell us about the recording
                </h2>
                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <FormField
                    id="song_title" label="Working title" required maxLength={240}
                    value={form.song_title}
                    onChange={(v) => setField("song_title", v)}
                    error={errors.song_title}
                    dataTestId="submit-demo-field-song-title"
                  />
                  <FormField
                    id="alternate_title" label="Alternate title (optional)" maxLength={240}
                    value={form.alternate_title}
                    onChange={(v) => setField("alternate_title", v)}
                    dataTestId="submit-demo-field-alt-title"
                  />
                  <FormField
                    id="primary_genre" label="Primary genre" maxLength={80}
                    placeholder="e.g. Soul / R&amp;B, Gospel, Hip-Hop"
                    value={form.primary_genre}
                    onChange={(v) => setField("primary_genre", v)}
                    dataTestId="submit-demo-field-genre"
                  />
                  <FormField
                    id="approximate_completion_date" label="Approximate completion date" maxLength={80}
                    placeholder="e.g. Q4 2025"
                    value={form.approximate_completion_date}
                    onChange={(v) => setField("approximate_completion_date", v)}
                    dataTestId="submit-demo-field-completion"
                  />
                </div>
                <div className="mt-4">
                  <FormTextarea
                    id="development_notes" label="Development notes (optional)" maxLength={4000} rows={4}
                    placeholder="What stage is this in? What kind of development partnership are you envisioning?"
                    value={form.development_notes}
                    onChange={(v) => setField("development_notes", v)}
                    dataTestId="submit-demo-field-notes"
                  />
                </div>
              </div>

              {/* Section 3: Contributors / third-party elements */}
              <div data-testid="submit-demo-section-contributors">
                <Eyebrow>3 — Contributors &amp; sources</Eyebrow>
                <h2 className="mt-3" style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 24, fontWeight: 600 }}>
                  Everyone &amp; everything that touched it
                </h2>
                <p className="mt-3 text-[13px] leading-relaxed" style={{ color: `${CREAM}99` }}>
                  These fields exist so Kandake can verify origin, splits, and rights cleanly before
                  considering the recording for development. Be honest and as complete as you can — gaps are
                  fine; misrepresentation is not.
                </p>
                <div className="space-y-4 mt-6">
                  <FormTextarea
                    id="known_contributors" label="Known contributors (writers, producers, performers, vocalists, instrumentalists)"
                    maxLength={2000} rows={4}
                    placeholder="Names, roles, and known splits if any. One per line is fine."
                    value={form.known_contributors}
                    onChange={(v) => setField("known_contributors", v)}
                    dataTestId="submit-demo-field-contributors"
                  />
                  <FormTextarea
                    id="known_third_party_elements"
                    label="Known third-party elements (samples, interpolations, beats, AI-assisted sources, library cues, etc.)"
                    maxLength={2000} rows={4}
                    placeholder="If any. Disclose proactively — including AI-assisted writing, vocals, or instrumentation."
                    value={form.known_third_party_elements}
                    onChange={(v) => setField("known_third_party_elements", v)}
                    dataTestId="submit-demo-field-third-party"
                  />
                </div>
              </div>

              {/* Section 4: Files */}
              <div data-testid="submit-demo-section-files">
                <Eyebrow>4 — Files</Eyebrow>
                <h2 className="mt-3" style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 24, fontWeight: 600 }}>
                  Upload the material
                </h2>
                <p className="mt-3 text-[13px] leading-relaxed" style={{ color: `${CREAM}99` }}>
                  An audio file is required. Stems, lyrics, and supporting documents help Kandake review the
                  origin and rights position faster. Executable file types are rejected automatically.
                </p>
                <div className="space-y-3 mt-6">
                  <FilePicker
                    id="audio" kind="audio" slot="audio" required
                    label={FILE_KIND_LABELS.audio}
                    allowedHint={allowedHintFor("audio")}
                    capLabel={capLabelFor("audio")}
                    file={files.audio}
                    onPick={(f) => setFileSlot("audio", f)}
                    error={errors.file_audio}
                    dataTestId="submit-demo-file-audio"
                  />
                  <FilePicker
                    id="lyrics" kind="lyrics" slot="lyrics"
                    label={FILE_KIND_LABELS.lyrics}
                    allowedHint={allowedHintFor("lyrics")}
                    capLabel={capLabelFor("lyrics")}
                    file={files.lyrics}
                    onPick={(f) => setFileSlot("lyrics", f)}
                    dataTestId="submit-demo-file-lyrics"
                  />
                  <FilePicker
                    id="stems" kind="stems" slot="stems"
                    label={FILE_KIND_LABELS.stems}
                    allowedHint={allowedHintFor("stems")}
                    capLabel={capLabelFor("stems")}
                    file={files.stems}
                    onPick={(f) => setFileSlot("stems", f)}
                    dataTestId="submit-demo-file-stems"
                  />
                  <FilePicker
                    id="daw_project" kind="daw" slot="daw_project"
                    label={FILE_KIND_LABELS.daw}
                    allowedHint={allowedHintFor("daw")}
                    capLabel={capLabelFor("daw")}
                    file={files.daw_project}
                    onPick={(f) => setFileSlot("daw_project", f)}
                    dataTestId="submit-demo-file-daw"
                  />
                  <FilePicker
                    id="supporting_doc_1" kind="supporting" slot="supporting_doc_1"
                    label="Supporting document (optional, slot 1)"
                    allowedHint={allowedHintFor("supporting")}
                    capLabel={capLabelFor("supporting")}
                    file={files.supporting_doc_1}
                    onPick={(f) => setFileSlot("supporting_doc_1", f)}
                    dataTestId="submit-demo-file-supporting-1"
                  />
                  <FilePicker
                    id="supporting_doc_2" kind="supporting" slot="supporting_doc_2"
                    label="Supporting document (optional, slot 2)"
                    allowedHint={allowedHintFor("supporting")}
                    capLabel={capLabelFor("supporting")}
                    file={files.supporting_doc_2}
                    onPick={(f) => setFileSlot("supporting_doc_2", f)}
                    dataTestId="submit-demo-file-supporting-2"
                  />
                  <FilePicker
                    id="supporting_doc_3" kind="supporting" slot="supporting_doc_3"
                    label="Supporting document (optional, slot 3)"
                    allowedHint={allowedHintFor("supporting")}
                    capLabel={capLabelFor("supporting")}
                    file={files.supporting_doc_3}
                    onPick={(f) => setFileSlot("supporting_doc_3", f)}
                    dataTestId="submit-demo-file-supporting-3"
                  />
                </div>
                {totalSelectedBytes > 0 && (
                  <div className="mt-3 text-[11.5px]" style={{ color: `${CREAM}99` }} data-testid="submit-demo-total-bytes">
                    Total selected: {humanSize(totalSelectedBytes)}
                  </div>
                )}
              </div>

              {/* Section 5: Attestations */}
              <div data-testid="submit-demo-section-attestations">
                <Eyebrow>5 — Attestations</Eyebrow>
                <h2 className="mt-3" style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 24, fontWeight: 600 }}>
                  Confirm authority &amp; consent
                </h2>
                <div className="space-y-4 mt-6 rounded-md px-5 py-5"
                  style={{ background: INK_2, border: `1px solid ${GOLD}33` }}
                >
                  <AttestationCheckbox
                    id="att-authority"
                    checked={form.has_authority_to_submit}
                    onToggle={(v) => setField("has_authority_to_submit", v)}
                    error={errors.has_authority_to_submit}
                    dataTestId="submit-demo-att-authority"
                  >
                    I am the rights holder, or I have explicit authority from every contributor to submit this
                    recording on their behalf.
                  </AttestationCheckbox>
                  <AttestationCheckbox
                    id="att-rights"
                    checked={form.rights_and_origin_attested}
                    onToggle={(v) => setField("rights_and_origin_attested", v)}
                    error={errors.rights_and_origin_attested}
                    dataTestId="submit-demo-att-rights"
                  >
                    I have disclosed third-party elements honestly to the best of my knowledge — including
                    samples, interpolations, beat licenses, library cues, and any AI-assisted contributions.
                  </AttestationCheckbox>
                  <AttestationCheckbox
                    id="att-consent"
                    checked={form.review_consent}
                    onToggle={(v) => setField("review_consent", v)}
                    error={errors.review_consent}
                    dataTestId="submit-demo-att-consent"
                  >
                    I consent to internal compliance review of this material, including automated origin
                    screening and human review by authorised Kandake personnel.
                  </AttestationCheckbox>
                </div>

                <div className="mt-5 flex items-start gap-3 text-[12px]" style={{ color: `${CREAM}99` }}>
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5" style={{ color: GOLD }} aria-hidden="true" />
                  <p className="leading-relaxed">
                    Material is stored privately. Submission does not grant any licence, clearance, registration,
                    release approval, or commercial use. We do not share submitter information with third parties
                    outside of compliance review.
                  </p>
                </div>
              </div>

              {/* Honeypot — must remain empty */}
              <div
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}
              >
                <label htmlFor="sd-website">Website (do not fill)</label>
                <input
                  id="sd-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={(e) => setField("website", e.target.value)}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                <p style={{ color: `${CREAM}99`, fontSize: 11.5, lineHeight: 1.55, maxWidth: 480 }}>
                  Please do not upload passwords, banking information, or copies of unrelated commercial
                  releases. Files are private and reviewed only by authorised Kandake personnel.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-[12px] uppercase tracking-[0.22em] transition-opacity hover:opacity-90 disabled:opacity-50"
                  style={{ background: GOLD, color: INK, border: `1px solid ${GOLD}` }}
                  data-testid="submit-demo-submit"
                >
                  <Send className="w-3.5 h-3.5" />
                  {submitting ? "Uploading\u2026" : "Submit demo"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
